// src/store/sagas/mainSaga.ts
import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
    listSectorTabs,
    getLatestDigest,
    getFeatureMetrics,
    listSectorSignals,
    getReactionProfile,
    getReactionWindows,
    listInsights,
    type SectorTab,
    type LatestDigest,
    type FeatureMetricGroup,
    type SignalCard,
    type ReactionProfileDTO,
    type ReactionWindowsDTO,
    type InsightRow,
} from '../../api/mainApi';

import {
    fetchSectorTabsRequested,  fetchSectorTabsSucceeded,  fetchSectorTabsFailed,
    fetchLatestDigestRequested, fetchLatestDigestSucceeded, fetchLatestDigestFailed,
    fetchFeatureMetricsRequested, fetchFeatureMetricsSucceeded, fetchFeatureMetricsFailed,
    fetchSectorSignalsRequested, fetchSectorSignalsSucceeded, fetchSectorSignalsFailed,
    fetchReactionProfileRequested, fetchReactionProfileSucceeded, fetchReactionProfileFailed,
    fetchReactionWindowsRequested, fetchReactionWindowsSucceeded, fetchReactionWindowsFailed,
    fetchInsightListRequested, fetchInsightListSucceeded, fetchInsightListFailed,
} from '../slice/mainSlice';

/* Workers */
function* fetchSectorTabsWorker() {
    try {
        const data: SectorTab[] = yield call(listSectorTabs as any);
        yield put(fetchSectorTabsSucceeded(data));
    } catch (e: any) {
        yield put(fetchSectorTabsFailed(e?.message ?? '섹터 탭 로드 실패'));
    }
}
function* fetchLatestDigestWorker() {
    try {
        const data: LatestDigest = yield call(getLatestDigest as any);
        yield put(fetchLatestDigestSucceeded(data));
    } catch (e: any) {
        yield put(fetchLatestDigestFailed(e?.message ?? '집계 요약 로드 실패'));
    }
}
function* fetchFeatureMetricsWorker() {
    try {
        const data: FeatureMetricGroup[] = yield call(getFeatureMetrics as any);
        yield put(fetchFeatureMetricsSucceeded(data));
    } catch (e: any) {
        yield put(fetchFeatureMetricsFailed(e?.message ?? '기능 메트릭 로드 실패'));
    }
}
function* fetchSectorSignalsWorker(action: ReturnType<typeof fetchSectorSignalsRequested>) {
    const { sector, limit = 10, offset = 0 } = action.payload || ({} as any);
    try {
        const items: SignalCard[] = yield call(listSectorSignals as any, { sector, limit, offset });
        yield put(fetchSectorSignalsSucceeded({ sector, items, limit, offset }));
    } catch (e: any) {
        yield put(fetchSectorSignalsFailed({ sector, message: e?.message ?? '섹터 시그널 로드 실패' }));
    }
}
function* fetchReactionProfileWorker(action: ReturnType<typeof fetchReactionProfileRequested>) {
    const { sector } = action.payload;
    try {
        // 문자열로 직접 전달
        const profile: ReactionProfileDTO = yield call(getReactionProfile as any, sector);
        yield put(fetchReactionProfileSucceeded({ sector, profile }));
    } catch (e: any) {
        yield put(fetchReactionProfileFailed({ sector, message: e?.message ?? '반응 프로파일 로드 실패' }));
    }
}
function* fetchReactionWindowsWorker(action: ReturnType<typeof fetchReactionWindowsRequested>) {
    const { feedId } = action.payload;
    try {
        // 숫자/문자 그대로 전달
        const data: ReactionWindowsDTO = yield call(getReactionWindows as any, feedId);
        yield put(fetchReactionWindowsSucceeded({ feedId, data }));
    } catch (e: any) {
        yield put(fetchReactionWindowsFailed({ feedId, message: e?.message ?? '윈도우 성과 로드 실패' }));
    }
}
function* fetchInsightListWorker(action: ReturnType<typeof fetchInsightListRequested>) {
    const { limit = 20, offset = 0 } = action.payload || ({} as any);
    try {
        const items: InsightRow[] = yield call(listInsights as any, { limit, offset });
        yield put(fetchInsightListSucceeded({ items, limit, offset }));
    } catch (e: any) {
        yield put(fetchInsightListFailed(e?.message ?? '인사이트 목록 로드 실패'));
    }
}

/* Watchers & Root */
function* watchSectorTabs()        { yield takeLatest(fetchSectorTabsRequested.type, fetchSectorTabsWorker); }
function* watchLatestDigest()      { yield takeLatest(fetchLatestDigestRequested.type, fetchLatestDigestWorker); }
function* watchFeatureMetrics()    { yield takeLatest(fetchFeatureMetricsRequested.type, fetchFeatureMetricsWorker); }
function* watchSectorSignals()     { yield takeLatest(fetchSectorSignalsRequested.type, fetchSectorSignalsWorker); }
function* watchReactionProfile()   { yield takeLatest(fetchReactionProfileRequested.type, fetchReactionProfileWorker); }
function* watchReactionWindows()   { yield takeLatest(fetchReactionWindowsRequested.type, fetchReactionWindowsWorker); }
function* watchInsightList()       { yield takeLatest(fetchInsightListRequested.type, fetchInsightListWorker); }

export default function* mainSaga() {
    yield all([
        watchSectorTabs(),
        watchLatestDigest(),
        watchFeatureMetrics(),
        watchSectorSignals(),
        watchReactionProfile(),
        watchReactionWindows(),
        watchInsightList(),
    ]);
}
